import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/Auth';

import {
  GET_TOTP,
  DISABLE_TOTP,
  ENABLE_MASTER_KEY,
  DISABLE_MASTER_KEY,
} from '../../http/apollo-queries';

import ScanQR from '../../components/Scan-QR/Scan-QR';
import { Button, Card, Icon, ToggleButton } from '../../components/UI';
import TooltipIcon from '../../components/InfoTooltip/Tooltip-icon';

const Account2FA = () => {
  const { t } = useTranslation();
  const { user, refetch } = useAuth();

  const [getTOTP, { loading: getTOTPLoading, data: getTOTPData }] =
    useLazyQuery(GET_TOTP);

  const [disableTOTP, { loading: disableTOTPLoading, data: disableTOTPData }] =
    useMutation(DISABLE_TOTP, {
      onError: (error) => {
        toast.error(t('account.error.disableOtp'));
      },
      onCompleted: (data) => {
        toast.success(t('account.success.disableOtp'));
      },
    });

  const [
    enableMasterKey,
    { data: enableMasterKeyData, loading: enableMasterKeyLoading },
  ] = useMutation(ENABLE_MASTER_KEY, {
    onError: (error) => {
      toast.error(t('account.error.enableMasterKey'));
    },
    onCompleted: (data) => {
      toast.success(t('account.success.enableMasterKey'));
    },
  });

  const [disableMasterKey, { loading: disableMasterKeyLoading }] = useMutation(
    DISABLE_MASTER_KEY,
    {
      onError: (error) => {
        toast.error(t('account.error.disableMasterKey'));
      },
      onCompleted: (data) => {
        toast.success(t('account.success.disableMasterKey'));
      },
    }
  );

  const [isTOTPEnabled, setIsTOTPEnabled] = useState(user?.MFAStatus);
  const [showQRCode, setShowQRCode] = useState(false);
  const [isMasterKeyEnabled, setIsMasterKeyEnabled] = useState(
    user?.masterKeyStatus
  );
  const [masterKey, setMasterKey] = useState('');

  useEffect(() => {
    if (disableTOTPData) {
      if (disableTOTPData.disableTOTP.status === 'SUCCESS') {
        setIsTOTPEnabled(false);
      }
    }
  }, [disableTOTPData]);

  useEffect(() => {
    if (getTOTPData) {
      setShowQRCode(true);
    }
  }, [getTOTPData]);

  const checkAndGetTOTPData = () => {
    if (getTOTPData) {
      setShowQRCode(true);
    } else {
      getTOTP();
    }
  };

  useEffect(() => {
    if (enableMasterKeyData) {
      const { enableMasterKey } = enableMasterKeyData;
      const { key } = enableMasterKey;
      setMasterKey(key);
    }
  }, [enableMasterKeyData]);

  const enableMasterKeyHandler = () => {
    enableMasterKey().then(() => {
      setIsMasterKeyEnabled(true);
      refetch();
    });
  };

  const disableMasterKeyHandler = () => {
    disableMasterKey().then(() => {
      setIsMasterKeyEnabled(false);
      refetch();
    });
  };

  const renderMasterKeyToggle = () => (
    <div className="flex border-t border-opacity-50 border-grey-30 justify-start align-center px-5 pt-5 mt-5 -mx-5">
      <div className="flex flex-col gap-5 w-full">
        <div className="flex flex-col justify-between align-center">
          <h3 className="font-bold text-sm uppercase">master key</h3>
          <p className="text-grey-50">
            A Master key acts as an account recovery key if you lose or forget
            your password or sign-in 2FA method.
          </p>
        </div>
        <ToggleButton
          onActivate={enableMasterKeyHandler}
          onDeactivate={disableMasterKeyHandler}
          activatedLabel="Disable master key"
          deactivatedLabel="Enable master key"
          renderLabelOn="right"
          isActivated={isMasterKeyEnabled}
          isLoading={enableMasterKeyLoading || disableMasterKeyLoading}
        />
        {isMasterKeyEnabled && masterKey && (
          <div className="flex flex-col">
            <span className="text-xs font-semibold">Your Master Key</span>
            <div className="flex justify-between items-center w-full h-11 rounded-lg border border-grey-10 px-3 mt-2">
              <span className="text-sm">{masterKey}</span>
              <Icon
                className="cursor-pointer"
                color="black"
                svgName={'copy'}
                onClick={() => {
                  navigator.clipboard.writeText(masterKey);
                }}
              />
            </div>
            <div className="relative flex w-8/12 rounded-xl bg-grey-5 pl-12 p-5 mt-4">
              <TooltipIcon
                tooltipIconClassname="absolute left-4 top-5"
                variant="negative"
              />
              <p className="font-semibold text-sm text-grey-50">
                {t('account.2fa.masterKeyDescription')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Card className="mt-4" title={t('account.2fa.title')}>
        <div className="flex justify-start align-center">
          <div className="flex flex-col gap-5 w-full">
            <div className="flex flex-col justify-between align-center">
              {isTOTPEnabled ? (
                <h3 className="font-semibold text-sm">
                  {t('account.2fa.active')}
                </h3>
              ) : (
                <h3 className="font-semibold text-sm">
                  {t('account.2fa.inactive')}
                </h3>
              )}
              <p className="text-grey-50">{t('account.2fa.description')}</p>
            </div>
            <div className="flex justify-end">
              {isTOTPEnabled ? (
                <Button
                  type="button"
                  variant="primary"
                  loading={disableTOTPLoading}
                  onClick={() => disableTOTP()}
                >
                  {t('account.2fa.disable')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  loading={getTOTPLoading}
                  onClick={() => checkAndGetTOTPData()}
                >
                  {t('account.2fa.enable')}
                </Button>
              )}
            </div>
          </div>
        </div>
        {isTOTPEnabled && renderMasterKeyToggle()}
      </Card>

      {showQRCode && getTOTPData && (
        <ScanQR
          qrCode={getTOTPData.getTOTP.qr}
          secret={getTOTPData.getTOTP.secret}
          handleSuccess={() => setIsTOTPEnabled(true)}
          handleCancelClick={() => setShowQRCode(false)}
        />
      )}
    </>
  );
};

export { Account2FA };
